
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { FacetValueViewObject, FacetViewObject, SelectionTypeViewObject } from '@/types/apiServerContract';
import urlHelperService, { UrlFacets } from '@/core/urlHelper.service';
import gtmService from '@/project/tracking/gtm/GTM.service';
import escEventService from '@/core/esc-event.service';

import { BRAND_FACET_KEY } from '@/project/search/facets/constants';
import dictionaryService from '@/core/translation/dictionary.service';
import { PRICE_FACET_KEY } from '@/project/search/constants';
import debounce from 'lodash/debounce';

@Component
export default class FacetPicker extends Vue {
    @Prop({ required: true, type: Object })
    protected facet!: FacetViewObject;

    @Prop({ type: Boolean, default: false })
    protected last!: boolean;

    protected active: boolean = false;
    protected values: FacetValueViewObject[] = []
    protected searchTerm = '';
    protected valueRanges = {
        rangeFrom: {
            minValue: 0,
            value: '',
            invalid: false,
            getValue: function() {
                const value = parseFloat(this.value);
                return isNaN(value) ? this.minValue : value;
            }
        },
        rangeTo: {
            maxValue: 0,
            value: '',
            invalid: false,
            getValue: function() {
                const value = parseFloat(this.value);
                return isNaN(value) ? this.maxValue : value;
            }
        },
        errors: [] as string[],
        reset: function() {
            this.errors = [];
            this.rangeFrom.value = '';
            this.rangeFrom.invalid = false;
            this.rangeTo.value = '';
            this.rangeTo.invalid = false;
        },
        isValid: function() {
            return (this.rangeFrom.value || this.rangeTo.value) && this.errors.length === 0;
        }
    }

    protected get isRangeFacetting() {
        return this.valueRanges.isValid();
    }

    protected get isRangeCompatible() {
        return this.facet.isRangeCompatible;
    }

    protected get isNotToggle(): boolean {
        return this.facet.selectionType !== SelectionTypeViewObject.Toggle;
    }

    protected get isSingleSelect(): boolean {
        return this.facet.selectionType === SelectionTypeViewObject.SingleSelect;
    }

    protected get shouldUpdateLastFacet(): boolean {
        return this.facet.isFacet;
    }

    protected get isBrandFacet() {
        return this.facet.facetId === BRAND_FACET_KEY;
    }

    protected get isPriceFilter() {
        return this.facet.facetId === PRICE_FACET_KEY;
    }

    private syncRangesWithUrlDebounce = debounce(this.syncRangesWithUrl, 300);

    protected syncRangesWithUrl(pushToUrl: boolean = true) {
        this.valueRanges.errors = [];
        this.valueRanges.rangeFrom.invalid = false;
        this.valueRanges.rangeTo.invalid = false;

        if (this.valueRanges.rangeFrom.value || this.valueRanges.rangeTo.value) {
            const rangeFrom = parseFloat(this.valueRanges.rangeFrom.value);
            const rangeTo = parseFloat(this.valueRanges.rangeTo.value);

            if (this.valueRanges.rangeFrom.value && (isNaN(rangeFrom) || rangeFrom < this.valueRanges.rangeFrom.minValue)) {
                this.valueRanges.rangeFrom.invalid = true;
                this.valueRanges.errors.push('Search.Facets.RangeFilterChoices.RangeFrom.OutOfRange');
            }

            if (this.valueRanges.rangeTo.value && (isNaN(rangeTo) || rangeTo > this.valueRanges.rangeTo.maxValue)) {
                this.valueRanges.rangeTo.invalid = true;
                this.valueRanges.errors.push('Search.Facets.RangeFilterChoices.RangeTo.OutOfRange');
            }

            if (this.valueRanges.rangeTo.value && this.valueRanges.rangeTo.value && !isNaN(rangeFrom) && !isNaN(rangeTo)) {
                if (rangeFrom > rangeTo || rangeTo < rangeFrom) {
                    this.valueRanges.rangeFrom.invalid = true;
                    this.valueRanges.rangeTo.invalid = true;
                    this.valueRanges.errors.push('Search.Facets.RangeFilterChoices.OutOfRange');
                } else {
                    const anyInRange = this.facet.values
                        .map(x => parseFloat(x.key))
                        .filter(x => !isNaN(x))
                        .some(x => x >= rangeFrom && x <= rangeTo);

                    if (!anyInRange) {
                        this.valueRanges.errors.push('Search.Facets.RangeFilterChoices.EmptyRange');
                    }
                }
            }

            if (this.valueRanges.isValid() && pushToUrl) {
                urlHelperService.setFacetRanges({
                    facetId: this.facet.facetId,
                    rangeFrom: this.valueRanges.rangeFrom.getValue().toString(),
                    rangeTo: this.valueRanges.rangeTo.getValue().toString(),
                    updateLastFacet: this.shouldUpdateLastFacet
                });
            }
        }
    }

    private resetFilter() {
        if (this.isRangeFacetting) {
            this.valueRanges.reset();
            urlHelperService.setFacetRanges({
                facetId: this.facet.facetId,
                rangeFrom: this.valueRanges.rangeFrom.value,
                rangeTo: this.valueRanges.rangeTo.value,
                updateLastFacet: this.shouldUpdateLastFacet
            });
        } else {
            urlHelperService.setFacetValues(this.facet.facetId, [], this.shouldUpdateLastFacet);
            this.values.forEach(value => value.selected = false);
        }
    }

    public open() {
        if (!this.facet.enabled) return;
        if (!this.isNotToggle) return;
        if (this.facet.values.length === 0) return;

        this.active = true;
        escEventService.register(this.close);

        this.$emit('active', true);
    }

    public close() {
        if (!this.isNotToggle) return;

        this.active = false;
        escEventService.unregister(this.close);

        setTimeout(() => this.$emit('active', false), 200); // appears to be a slight delay from searching...
    }

    protected updateFacetValue(value: FacetValueViewObject) {
        if (value.selected) {
            gtmService.trackFacet(this.facet, value);
        }

        if (this.isSingleSelect) {
            const values = value.selected ? [value.key] : [];
            urlHelperService.setFacetValues(this.facet.facetId, values, this.shouldUpdateLastFacet);
        } else {
            urlHelperService.updateFacetValue(this.facet.facetId, value.key, value.selected, this.shouldUpdateLastFacet);
        }
    }

    protected synchronizeFacetsWithUrl() {
        const urlFacets: UrlFacets = urlHelperService.getFacets();
        const activeValues = urlFacets[this.facet.facetId];
        if (activeValues) {
            this.values.forEach(v => v.selected = activeValues.includes(v.key));
        } else {
            this.values.forEach(v => v.selected = false);
        }
    }

    protected getFacetName(facet: FacetViewObject) : string {
        if (facet.facetDisplayLabel) {
            return dictionaryService.get(facet.facetDisplayLabel);
        }

        return facet.facetDisplayName;
    }

    private getFacetValueDisplayName(value: FacetValueViewObject): string {
        if (dictionaryService.exists(value.displayLabel)) {
            return dictionaryService.get(value.displayLabel);
        }

        return value.displayName;
    }

    protected get filteredFacetValues(): FacetValueViewObject[] {
        const result = this.values.filter(v => {
            return v.displayName.toLowerCase().includes(this.searchTerm.toLowerCase());
        });
        return result;
    }

    protected get showSearchField(): boolean {
        return this.values.length > 10;
    }

    @Watch('facet', {
        immediate: true
    })
    onFacetChange(facet: FacetViewObject) {
        if (this.isRangeCompatible) {
            const numericValues = this.facet.values.map(x => parseFloat(x.key)).filter(x => !isNaN(x));
            this.valueRanges.rangeFrom.minValue = Math.min(...numericValues);
            this.valueRanges.rangeTo.maxValue = Math.max(...numericValues);
            this.valueRanges.rangeFrom.value = this.facet.rangeFrom?.toString() ?? '';
            this.valueRanges.rangeTo.value = this.facet.rangeTo?.toString() ?? '';
            this.syncRangesWithUrl(false);
        }

        this.values = facet.values.map(v => ({
            ...v,
            displayName: this.getFacetValueDisplayName(v)
        }));
        this.synchronizeFacetsWithUrl();
    }
}
